import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";

// Utilitário para fetch autenticado
function apiFetch(url, options = {}) {
  const token = localStorage.getItem("token");
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  return fetch(url, { ...options, headers });
}

/** ==== Helpers de data (LOCAL, sem UTC) ==== */
const pad2 = (n) => String(n).padStart(2, "0");
const ymdLocal = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const ymKeyLocal = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;

/** ==== Helpers de string/status ==== */
const normalize = (s) =>
  (s ?? "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .trim();

/** ==== Parser de moeda (suporta número, "1234.56", "1.234,56", "R$ 1.234,56") ==== */
function parseMoneyBR(value) {
  if (value == null) return 0;
  if (typeof value === "number") return isFinite(value) ? value : 0;
  let s = String(value).trim();
  s = s.replace(/R\$\s?/gi, "").replace(/\s/g, "");
  if (s.includes(",") && s.includes(".")) {
    // "1.234,56" -> "1234,56"
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (s.includes(",")) {
    // "1234,56" -> "1234.56"
    s = s.replace(",", ".");
  }
  const n = parseFloat(s);
  return Number.isNaN(n) ? 0 : n;
}

/** ==== Card ==== */
const CardIndicador = ({ titulo, valor, icone, cor }) => (
  <motion.div
    whileHover={{ scale: 1.02, y: -1 }}
    transition={{ type: "spring", stiffness: 200, damping: 20 }}
    className={`flex flex-col justify-between bg-white rounded-xl shadow px-3 py-2 border-l-4 ${cor} hover:shadow-lg w-full h-[68px]`}
    style={{ minWidth: 0 }}
  >
    <div className="flex items-center gap-2">
      <span className="text-xl">{icone}</span>
      <span className="text-[11px] text-gray-500 font-semibold text-center leading-tight break-words">
        {titulo}
      </span>
    </div>
    <div className="flex-1 flex items-center justify-center">
      <span className="text-lg font-bold text-gray-800">{valor}</span>
    </div>
  </motion.div>
);

/** ==== Componente ==== */
export default function IndicadoresLeads({
  leads = [],
  empresaId, // passe empresaAtiva.id (não fixo)
}) {
  // Estados
  const [agendamentosHoje, setAgendamentosHoje] = useState(0);
  const [agendamentosAmanha, setAgendamentosAmanha] = useState(0);
  const [agendamentosMes, setAgendamentosMes] = useState(0);

  const [totalFechadoMes, setTotalFechadoMes] = useState(0); // soma em R$
  const [qtdFechadoTotalMes, setQtdFechadoTotalMes] = useState(0); // Fechado + receb. no mês
  const [qtdFechadoParcialMes, setQtdFechadoParcialMes] = useState(0); // Fechado Parcial + receb. no mês

  // 🔵 NOVO: totais de oportunidades vindos do endpoint
  const [totalOportunidades, setTotalOportunidades] = useState(0);
  const [totalAbertas, setTotalAbertas] = useState(0);
  const [totalParcialSaldo, setTotalParcialSaldo] = useState(0);

  // Moeda
  const moeda = (v) =>
    isNaN(v)
      ? "R$ 0,00"
      : Number(v).toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        });

  // Datas atuais (LOCAL)
  const now = new Date();
  const ymNow = ymKeyLocal(now); // ex: "2025-08"

  /** ===============================
   *  Indicadores simples a partir do array de leads
   *  =============================== */
  const {
    leadsAtivasCount,
    migrouConcorrenciaCount,
    perdidosCount,
    potencialAltoCount,
  } = useMemo(() => {
    const normLeads = leads.map((l) => ({
      ...l,
      _status: normalize(l.status_negociacao),
      _pot: normalize(l.potencial),
    }));

    const isPerdido = (s) => s === "PERDIDO";
    const isMigrou = (s) => s === "MIGROU CONCORRENCIA";

    const leadsAtivasCount = normLeads.filter(
      (l) => !isPerdido(l._status) && !isMigrou(l._status)
    ).length;

    const migrouConcorrenciaCount = normLeads.filter((l) =>
      isMigrou(l._status)
    ).length;

    const perdidosCount = normLeads.filter((l) => isPerdido(l._status)).length;

    const potencialAltoCount = normLeads.filter(
      (l) =>
        l._pot === "ALTO" &&
        !isPerdido(l._status) &&
        !isMigrou(l._status) &&
        normalize(l.status_negociacao) !== "FECHADO" &&
        normalize(l.status_negociacao) !== "FECHADO PARCIAL"
    ).length;

    return {
      leadsAtivasCount,
      migrouConcorrenciaCount,
      perdidosCount,
      potencialAltoCount,
    };
  }, [leads]);

  /** ===============================
   *  Conjuntos por status (status ATUAL do array de leads)
   *  =============================== */
  const setFechado = useMemo(() => {
    const ids = leads
      .filter((l) => normalize(l.status_negociacao) === "FECHADO")
      .map((l) => String(l.id));
    return new Set(ids);
  }, [leads]);

  const setFechadoParcial = useMemo(() => {
    const ids = leads
      .filter((l) => normalize(l.status_negociacao) === "FECHADO PARCIAL")
      .map((l) => String(l.id));
    return new Set(ids);
  }, [leads]);

  /** ===============================
   *  FINANCEIRO (mês atual): total R$ + contagens (1 por lead)
   *  - mês filtrado por STRING 'YYYY-MM' para evitar timezone
   *  - soma APENAS recebimentos cujas leads estão Fechado/Parcial
   *  =============================== */
  useEffect(() => {
    let alive = true;

    async function fetchFinanceiroMes() {
      if (!empresaId) {
        if (alive) {
          setTotalFechadoMes(0);
          setQtdFechadoTotalMes(0);
          setQtdFechadoParcialMes(0);
        }
        return;
      }

      try {
        const res = await apiFetch(
          `https://api.captacrm.com.br/receb_leads/financeiro/${empresaId}`
        );
        if (!res.ok) {
          if (alive) {
            setTotalFechadoMes(0);
            setQtdFechadoTotalMes(0);
            setQtdFechadoParcialMes(0);
          }
          return;
        }

        const recebimentos = await res.json();
        const list = Array.isArray(recebimentos) ? recebimentos : [];

        // 🔒 Filtra por mês atual via string 'YYYY-MM'
        const recMes = list.filter((r) => {
          const ds = (r?.data_recebimento || "").toString();
          const ym = ds.slice(0, 7); // "YYYY-MM"
          return ym === ymNow;
        });

        // Conjunto de lead_ids que tiveram recebimento no mês
        const leadIdsMes = new Set(
          recMes
            .map((r) => r?.lead_id)
            .filter((id) => id !== null && id !== undefined)
            .map((id) => String(id))
        );

        // ✅ Contagens independentes (como na SQL)
        const countFechado = [...leadIdsMes].filter((id) =>
          setFechado.has(id)
        ).length;
        const countParcial = [...leadIdsMes].filter((id) =>
          setFechadoParcial.has(id)
        ).length;

        // ✅ Soma em R$ APENAS para leads com status Fechado/Parcial
        const total = recMes
          .filter(
            (r) =>
              setFechado.has(String(r.lead_id)) ||
              setFechadoParcial.has(String(r.lead_id))
          )
          .reduce((acc, r) => acc + parseMoneyBR(r.valor), 0);

        if (alive) {
          setTotalFechadoMes(total);
          setQtdFechadoTotalMes(countFechado);
          setQtdFechadoParcialMes(countParcial);
        }
      } catch {
        if (alive) {
          setTotalFechadoMes(0);
          setQtdFechadoTotalMes(0);
          setQtdFechadoParcialMes(0);
        }
      }
    }

    fetchFinanceiroMes();
    return () => {
      alive = false;
    };
  }, [empresaId, ymNow, setFechado, setFechadoParcial]);

  /** ===============================
   *  🔵 NOVO: Oportunidades (total, abertas e parcial saldo) via endpoint
   *  =============================== */
  useEffect(() => {
    let alive = true;
    async function fetchOportunidades() {
      if (!empresaId) {
        if (alive) {
          setTotalOportunidades(0);
          setTotalAbertas(0);
          setTotalParcialSaldo(0);
        }
        return;
      }
      try {
        const res = await apiFetch(
          `https://api.captacrm.com.br/receb_leads/oportunidades/empresa/${empresaId}`
        );
        if (!res.ok) {
          if (alive) {
            setTotalOportunidades(0);
            setTotalAbertas(0);
            setTotalParcialSaldo(0);
          }
          return;
        }
        const data = await res.json();
        if (alive) {
          setTotalOportunidades(Number(data.total_oportunidades || 0));
          setTotalAbertas(Number(data.total_abertas || 0));
          setTotalParcialSaldo(Number(data.total_parcial || 0));
        }
      } catch {
        if (alive) {
          setTotalOportunidades(0);
          setTotalAbertas(0);
          setTotalParcialSaldo(0);
        }
      }
    }
    fetchOportunidades();
    return () => {
      alive = false;
    };
  }, [empresaId]);

  /** ===============================
   *  Agendamentos (Hoje / Amanhã / Mês)
   *  =============================== */
  useEffect(() => {
    let alive = true;

    async function fetchAgendamentos() {
      if (!empresaId) {
        if (alive) {
          setAgendamentosHoje(0);
          setAgendamentosAmanha(0);
          setAgendamentosMes(0);
        }
        return;
      }

      try {
        const url = `https://api.captacrm.com.br/agendamentos/empresa/${empresaId}`;
        const res = await apiFetch(url);
        if (!res.ok) {
          if (alive) {
            setAgendamentosHoje(0);
            setAgendamentosAmanha(0);
            setAgendamentosMes(0);
          }
          return;
        }
        const agendamentos = await res.json();
        const list = Array.isArray(agendamentos) ? agendamentos : [];

        // Datas locais
        const hoje = new Date();
        const amanha = new Date(hoje);
        amanha.setDate(hoje.getDate() + 1);
        const hojeStr = ymdLocal(hoje);
        const amanhaStr = ymdLocal(amanha);
        const ymNowLocal = ymKeyLocal(hoje);

        const statusOk = new Set(["AGENDADO", "REAGENDADO"]);
        const isStatusOk = (s) => statusOk.has(normalize(s));

        const norm = list.filter(
          (a) => a && a.data_agendamento && isStatusOk(a.status_agendamento)
        );

        const hojeCount = norm.filter(
          (a) => ymdLocal(new Date(a.data_agendamento)) === hojeStr
        ).length;
        const amanhaCount = norm.filter(
          (a) => ymdLocal(new Date(a.data_agendamento)) === amanhaStr
        ).length;

        // Mês (1 por lead no mês atual)
        const doMes = norm.filter(
          (a) =>
            (a?.data_agendamento || "").toString().slice(0, 7) === ymNowLocal
        );
        const porLeadMaisRecente = {};
        for (const a of doMes) {
          if (!a.lead_id) continue;
          const key = String(a.lead_id);
          if (
            !porLeadMaisRecente[key] ||
            new Date(a.data_agendamento) >
              new Date(porLeadMaisRecente[key].data_agendamento)
          ) {
            porLeadMaisRecente[key] = a;
          }
        }
        const mesCount = Object.keys(porLeadMaisRecente).length;

        if (alive) {
          setAgendamentosHoje(hojeCount);
          setAgendamentosAmanha(amanhaCount);
          setAgendamentosMes(mesCount);
        }
      } catch {
        if (alive) {
          setAgendamentosHoje(0);
          setAgendamentosAmanha(0);
          setAgendamentosMes(0);
        }
      }
    }

    fetchAgendamentos();
    return () => {
      alive = false;
    };
  }, [empresaId]);

  return (
    <div className="w-full grid grid-cols-11 gap-2 mb-4 px-0">
      <CardIndicador
        titulo="Leads Ativas"
        valor={leadsAtivasCount}
        icone="📋"
        cor="border-blue-500"
      />
      <CardIndicador
        titulo="Migrou Concorrência"
        valor={migrouConcorrenciaCount}
        icone="🏳️"
        cor="border-rose-500"
      />
      <CardIndicador
        titulo="Perdidos"
        valor={perdidosCount}
        icone="❌"
        cor="border-gray-400"
      />
      <CardIndicador
        titulo="Potencial Alto"
        valor={potencialAltoCount}
        icone="🔥"
        cor="border-orange-500"
      />

      <CardIndicador
        titulo="Mês"
        valor={agendamentosMes}
        icone="🗓️"
        cor="border-yellow-500"
      />
      <CardIndicador
        titulo="Hoje"
        valor={agendamentosHoje}
        icone="📆"
        cor="border-cyan-500"
      />
      <CardIndicador
        titulo="Amanhã"
        valor={agendamentosAmanha}
        icone="⏭️"
        cor="border-pink-500"
      />

      {/* Contagens (base: recebimentos do mês + status atual da lead) */}
      <CardIndicador
        titulo="Qtd Total"
        valor={qtdFechadoTotalMes}
        icone="🔒"
        cor="border-green-700"
      />
      <CardIndicador
        titulo="Qtd Parcial"
        valor={qtdFechadoParcialMes}
        icone="🟢"
        cor="border-emerald-500"
      />

      {/* Soma em R$ (financeiro do mês, apenas leads Fechado/Parcial) */}
      <CardIndicador
        titulo="Total Fechado"
        valor={moeda(totalFechadoMes)}
        icone="💰"
        cor="border-orange-500"
      />

      {/* 🔵 Oportunidades (total vindo do endpoint oficial) */}
      <CardIndicador
        titulo="Oportunidades"
        valor={moeda(totalOportunidades)}
        icone="📊"
        cor="border-blue-700"
      />
    </div>
  );
}
